function SalaryRangeBars({ jobs }) {
  const ranges = jobs
    .map(job => parseSalaryRange(job.salary))
    .filter(Boolean);

  if (ranges.length === 0) {
    return (
      <div className="dash-empty">
        <div className="dash-empty-title">No salary data yet</div>
        <div className="dash-empty-text">Add salary ranges like 120k–150k to see your average range here.</div>
      </div>
    );
  }

  const minSalary = Math.min(...ranges.map(range => range.low));
  const maxSalary = Math.max(...ranges.map(range => range.high));
  const avgMidpoint = Math.round(
    ranges.reduce((sum, range) => sum + ((range.low + range.high) / 2), 0) / ranges.length
  );
  const averagePosition = 50;

  return (
    <div className="salary-range-chart">
      <div className="salary-range-summary">
        <span>${minSalary.toLocaleString()}</span>
        <span>${maxSalary.toLocaleString()}</span>
      </div>
      <div className="salary-range-pill">
        <div
          className="salary-range-average"
          style={{ left: `${averagePosition}%` }}
        >
          <span>${avgMidpoint.toLocaleString()}</span>
        </div>
      </div>
      <div className="salary-range-labels">
        <span>minimum</span>
        <span>average midpoint</span>
        <span>maximum</span>
      </div>
    </div>
  );
}


// Conservative typo map for dashboard grouping only.
// Patterns are case-insensitive; fix is the lowercase canonical word.
const TITLE_TYPO_MAP = [
  [/\bdesiger\b/gi, 'designer'],
];

function normalizeTitleKey(raw) {
  let s = String(raw || '')
    .replace(/[​-‍﻿­⁠]/g, '') // strip invisible chars
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  for (const [re, fix] of TITLE_TYPO_MAP) {
    s = s.replace(re, fix);
  }
  return s;
}

function cleanDisplayTitle(raw) {
  let s = String(raw || '')
    .replace(/[​-‍﻿­⁠]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
  for (const [re, fix] of TITLE_TYPO_MAP) {
    s = s.replace(re, match =>
      match[0] !== match[0].toLowerCase() ? fix[0].toUpperCase() + fix.slice(1) : fix
    );
  }
  return s;
}

function TitleBarChart({ jobs }) {
  const titleGroups = jobs.reduce((groups, job) => {
    const raw = String(job.title || '').trim();
    if (!raw) return groups;
    const key = normalizeTitleKey(raw);
    if (!key) return groups;
    const display = cleanDisplayTitle(raw);
    if (!groups.has(key)) {
      groups.set(key, { count: 0, display });
    }
    const group = groups.get(key);
    group.count++;
    // Prefer the display title from a job whose title needed no correction,
    // and among those, prefer the capitalized version over all-lowercase.
    if (display === raw) {
      const currentCapitalized = group.display[0] !== group.display[0].toLowerCase();
      const newCapitalized = raw[0] !== raw[0].toLowerCase();
      if (newCapitalized || !currentCapitalized) group.display = display;
    }
    return groups;
  }, new Map());
  const realRows = [...titleGroups.values()]
    .map(({ display, count }) => ({ label: display, count, placeholder: false }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 5);
  const rows = [
    ...realRows,
    ...Array.from({ length: 5 - realRows.length }, (_, index) => ({
      label: '',
      count: null,
      placeholder: true,
      key: `placeholder-${index}`,
    })),
  ];
  const max = Math.max(...realRows.map(row => row.count), 1);

  return (
    <div className={`title-bar-chart${realRows.length === 0 ? ' has-no-data' : ''}`}>
      {realRows.length === 0 && (
        <div
          className="title-bar-empty"
          style={{
            color: 'var(--title-bar-empty-color, var(--ink-faint))',
            fontSize: 11,
            fontWeight: 400,
            lineHeight: 1.35,
            WebkitTextSizeAdjust: 'none',
            textSizeAdjust: 'none',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No job title trends yet</div>
          Once you add jobs, your most common roles will show up here.
        </div>
      )}
      {rows.map(row => (
        <div className={`title-bar-row${row.placeholder ? ' is-placeholder' : ''}`} key={row.key || row.label}>
          <div className="title-bar-top">
            <span>{row.label}</span>
            <strong>{row.count}</strong>
          </div>
          <div className="title-bar-track">
            <div
              className="title-bar-fill"
              style={{ width: `${row.placeholder ? 0 : Math.max((row.count / max) * 100, 8)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function DeadZone({ jobs, onOpen, upsert }) {
  const today = todayISO();

  const stuck = jobs
    .filter(j => j.status === 'applied' && j.date)
    .map(j => ({ ...j, daysOld: daysBetween(j.date, today) }))
    .filter(j => j.daysOld != null && j.daysOld >= 40)
    .sort((a, b) => b.daysOld - a.daysOld);

  const groups = stuck.length > 0
    ? [{ key: '40plus', label: '40+ days', jobs: stuck }]
    : [];

  if (groups.length === 0) {
    return (
      <div className="dash-empty">
        <div className="dash-empty-title">Nothing ghost-worthy yet</div>
        <div className="dash-empty-text">Jobs that go quiet for 40+ days land here. Move them to Ghosted when you're ready to let go.</div>
      </div>
    );
  }

  return (
    <div className="dead-zone-groups">
      {groups.map(group => (
        <div key={group.key} className={`dead-zone-group dead-zone-${group.key}`}>
          <div className="dead-zone-group-head">
            <span>{group.label}</span>
            <span className="dead-zone-count">{group.jobs.length}</span>
          </div>
          {group.jobs.map(j => (
            <div key={j.id} className="reminder-item" onClick={() => onOpen(j)} style={{ cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{j.company}</div>
                <div className="ti" style={{ fontSize: 12 }}>{j.title}</div>
                <div className="why">{j.daysOld}d since applied</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Dashboard({ jobs, stats, reminders, onOpen, upsert, onAdd, onImportCSV, onDownloadTemplate }) {
  const deadZoneStuck = (() => {
    const today = todayISO();
    return jobs
      .filter(j => j.status === 'applied' && j.date)
      .map(j => ({ ...j, daysOld: daysBetween(j.date, today) }))
      .filter(j => j.daysOld != null && j.daysOld >= 40);
  })();

  const recent = [...jobs]
    .filter(j => j.date)
    .sort((a, b) => compareJobsByAppliedDate(a, b, 'desc'));

  const gridRef = React.useRef(null);
  const col1Ref = React.useRef(null);
  const [moreRecent, setMoreRecent] = React.useState(false);
  const [moreNudge,  setMoreNudge]  = React.useState(false);
  const [moreDead,   setMoreDead]   = React.useState(false);

  React.useEffect(() => {
    const col1 = col1Ref.current;
    const grid = gridRef.current;
    if (!col1 || !grid) return;
    const ro = new ResizeObserver(() => {
      grid.style.setProperty('--dash-col-height', col1.offsetHeight + 'px');
    });
    ro.observe(col1);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={`dash-grid${stats.total === 0 ? ' dash-grid-onboard' : ''}`} ref={gridRef}>
      <div className="dash-col mob-analytics-col" ref={col1Ref}>
        <div className="panel sketch-box">
          <h3><span className="material-icons" aria-hidden="true" style={{ fontSize: 14, color: 'var(--accent)', verticalAlign: 'middle', marginRight: 5 }}>attach_money</span>average salary range</h3>
          <SalaryRangeBars jobs={jobs} />
        </div>

        <div className="panel sketch-box">
          <h3><span className="material-icons" aria-hidden="true" style={{ fontSize: 14, color: 'var(--accent)', verticalAlign: 'middle', marginRight: 5 }}>work</span>job titles</h3>
          <TitleBarChart jobs={jobs} />
        </div>
      </div>

      <div className="dash-col">
        <div className="panel sketch-box">
          <h3><span className="material-icons" aria-hidden="true" style={{ fontSize: 14, color: 'var(--accent)', verticalAlign: 'middle', marginRight: 5 }}>history</span>recent activity</h3>
          <div className={`activity-list mob-dash-recent${moreRecent ? ' expanded' : ''}`}>
            {recent.length === 0 && (
              <div className="dash-start">
                <div className="dash-start-title">No activity yet</div>
                <div className="dash-start-text">Add your first job to start tracking applications, follow-ups, and outcomes.</div>
                <div className="dash-start-actions">
                  {onAdd && (
                    <button className="candy-btn link-pink" onClick={onAdd}>Add job</button>
                  )}
                  {onImportCSV && (
                    <button className="sketch-btn" onClick={onImportCSV}>Import CSV</button>
                  )}
                  {onDownloadTemplate && (
                    <button className="sketch-btn" onClick={onDownloadTemplate}>Download example CSV</button>
                  )}
                </div>
              </div>
            )}
            {recent.map(j => (
              <div key={j.id} className="job-card" onClick={() => onOpen(j)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <div>
                    <div className="co">{j.company}</div>
                    <div className="ti">{j.title}</div>
                  </div>
                  <span className={`pill ${STATUS_MAP[j.status]?.cls || ''}`}>
                    {STATUS_MAP[j.status]?.label || j.status}
                  </span>
                </div>
                <div className="meta">
                  <span><svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3}}><rect x="1" y="2" width="11" height="10" rx="1.5"/><path d="M4 1v2.5M9 1v2.5M1 5.5h11"/></svg>{prettyDate(j.date)}</span>
                  {j.salary  && <span><svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3}}><circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3v1m0 5v1M5 5.25C5 4.56 5.67 4 6.5 4S8 4.56 8 5.25 7.33 6.5 6.5 6.5 5 7.06 5 7.75 5.67 9 6.5 9 8 8.44 8 7.75"/></svg>{formatSalaryValue(j.salary)}</span>}
                  {j.contact && <span className="mob-hide-contact"><svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3}}><circle cx="6.5" cy="4.5" r="2.5"/><path d="M1 12.5c0-2.485 2.462-4.5 5.5-4.5s5.5 2.015 5.5 4.5"/></svg>{j.contact.split(',')[0]}</span>}
                </div>
              </div>
            ))}
            {recent.length > 3 && !moreRecent && (
              <button className="mob-view-all" type="button" onClick={() => setMoreRecent(true)}>
                View all {recent.length} →
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={`dash-col${stats.total === 0 ? ' mob-dash-stalezone-empty' : ''}`}>
        <div className="panel sketch-box">
          <h3><span className="material-icons" aria-hidden="true" style={{ fontSize: 14, color: 'var(--accent)', verticalAlign: 'middle', marginRight: 5 }}>schedule</span>stale — needs a nudge</h3>
          <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '-8px 0 10px' }}>
            applied with no response in {STALE_DAYS}+ days
          </p>
          <div className={`reminder-list mob-dash-nudge${moreNudge ? ' expanded' : ''}`}>
            {reminders.stale.length === 0 && (
              <div className="dash-empty">
                <div className="dash-empty-title">No follow-ups needed yet</div>
                <div className="dash-empty-text">Applied jobs with no response after {STALE_DAYS} days will show up here.</div>
              </div>
            )}
            {reminders.stale.slice(0, 8).map(j => (
              <div key={j.id} className="reminder-item" onClick={() => onOpen(j)} style={{ cursor: 'pointer' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{j.company}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{j.title}</div>
                  <div className="why">
                    {j.daysOld}d old{j.contact ? ` · follow up ${j.contact.split(',')[0]}` : ''}
                  </div>
                </div>
              </div>
            ))}
            {reminders.stale.length > 3 && !moreNudge && (
              <button className="mob-view-all" type="button" onClick={() => setMoreNudge(true)}>
                View all {reminders.stale.length} →
              </button>
            )}
          </div>
        </div>

        <div className="panel sketch-box">
          <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span><span className="material-icons" aria-hidden="true" style={{ fontSize: 14, color: 'var(--accent)', verticalAlign: 'middle', marginRight: 5 }}>warning</span>dead zone</span>
            {deadZoneStuck.length > 0 && upsert && (
              <button
                className="sketch-btn"
                style={{ fontSize: 11, padding: '2px 8px', marginLeft: 8 }}
                onClick={() => deadZoneStuck.forEach(j => upsert({ ...j, status: 'ghosted' }))}
              >
                Move all to Ghosted
              </button>
            )}
          </h3>
          <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '-8px 0 10px' }}>
            Jobs that have gone silent 40 days or more
          </p>
          <div className={`mob-dash-dead${moreDead ? ' expanded' : ''}`}>
            <DeadZone jobs={jobs} onOpen={onOpen} upsert={upsert} />
            {deadZoneStuck.length > 3 && !moreDead && (
              <button className="mob-view-all" type="button" onClick={() => setMoreDead(true)}>
                View all {deadZoneStuck.length} →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
